/**
 * Routing Rule Fields Configuration
 *
 * Defines available fields for routing rule conditions.
 * These fields represent data from records and CRM entities
 * that can be used to determine routing targets.
 */

import type { Field } from 'react-querybuilder';

import type { TFunction } from 'i18next';

/**
 * Field group keys (used as identifiers)
 */
export const ROUTING_FIELD_GROUP_KEYS = {
  record: 'record',
  analysis: 'analysis',
  crm_deal: 'crm_deal',
  crm_lead: 'crm_lead',
  crm_contact: 'crm_contact',
  crm_company: 'crm_company',
  metadata: 'metadata',
} as const;

/**
 * Field groups for better organization in UI
 * Returns translated group labels
 */
export function getRoutingFieldGroups(t: TFunction<string>) {
  return {
    record: t('routing.fieldGroups.record', 'Record Data'),
    analysis: t('routing.fieldGroups.analysis', 'Analysis Results'),
    crm_deal: t('routing.fieldGroups.crmDeal', 'CRM Deal'),
    crm_lead: t('routing.fieldGroups.crmLead', 'CRM Lead'),
    crm_contact: t('routing.fieldGroups.crmContact', 'CRM Contact'),
    crm_company: t('routing.fieldGroups.crmCompany', 'CRM Company'),
    metadata: t('routing.fieldGroups.metadata', 'Metadata'),
  } as const;
}

/**
 * Default routing rule fields (available for all projects)
 * CRM-specific fields will be dynamically added based on project CRM configuration
 */
export function getRoutingFields(t: TFunction<string>): Field[] {
  const groups = getRoutingFieldGroups(t);

  return [
    // ============================================================
    // Record Data
    // ============================================================
    {
      name: 'record.channel',
      label: t('routing.fields.channel', 'Channel'),
      inputType: 'select',
      values: [
        { name: 'call', label: t('routing.fields.phoneCall', 'Phone Call') },
        { name: 'chat', label: t('routing.fields.chat', 'Chat') },
      ],
      defaultValue: 'call',
      group: groups.record,
    },
    {
      name: 'record.direction',
      label: t('routing.fields.callDirection', 'Call Direction'),
      inputType: 'select',
      values: [
        { name: 'incoming', label: t('routing.fields.incoming', 'Incoming') },
        { name: 'outgoing', label: t('routing.fields.outgoing', 'Outgoing') },
      ],
      defaultValue: 'incoming',
      group: groups.record,
    },
    {
      name: 'record.duration',
      label: t('routing.fields.duration', 'Duration (seconds)'),
      inputType: 'number',
      defaultValue: 0,
      group: groups.record,
    },
    {
      name: 'record.agentId',
      label: t('routing.fields.agentId', 'Agent ID (Manager)'),
      inputType: 'text',
      group: groups.record,
    },
    {
      name: 'record.departmentId',
      label: t('routing.fields.department', 'Department (Manager)'),
      inputType: 'select',
      values: [], // Populated dynamically from useDepartments() hook
      group: groups.record,
    },
    {
      name: 'record.externalId',
      label: t('routing.fields.externalCrmId', 'External CRM ID'),
      inputType: 'text',
      group: groups.record,
    },
    {
      name: 'record.createdAt',
      label: t('routing.fields.recordDate', 'Record Date'),
      inputType: 'date',
      group: groups.record,
    },
    {
      name: 'record.agentName',
      label: t('routing.fields.agentName', 'Agent Name'),
      inputType: 'text',
      group: groups.record,
    },
    {
      name: 'record.crmEntityType',
      label: t('routing.fields.crmEntityType', 'CRM Entity Type'),
      inputType: 'select',
      values: [
        { name: 'deal', label: t('routing.fields.deal', 'Deal') },
        { name: 'lead', label: t('routing.fields.lead', 'Lead') },
        { name: 'contact', label: t('routing.fields.contact', 'Contact') },
        { name: 'company', label: t('routing.fields.company', 'Company') },
      ],
      group: groups.record,
    },

    // ============================================================
    // Analysis Results
    // ============================================================
    {
      name: 'analysis.qaScore',
      label: t('routing.fields.qaScore', 'QA Score'),
      inputType: 'number',
      defaultValue: 70,
      group: groups.analysis,
    },
    {
      name: 'analysis.sentimentScore',
      label: t('routing.fields.sentimentScore', 'Sentiment Score'),
      inputType: 'number',
      defaultValue: 0,
      group: groups.analysis,
      // Range: -100 to +100
    },
    {
      name: 'analysis.category',
      label: t('routing.fields.analysisCategory', 'Analysis Category'),
      inputType: 'select',
      values: [
        { name: 'sales', label: t('routing.fields.sales', 'Sales') },
        { name: 'support', label: t('routing.fields.support', 'Support') },
        { name: 'complaint', label: t('routing.fields.complaint', 'Complaint') },
        { name: 'inquiry', label: t('routing.fields.inquiry', 'Inquiry') },
      ],
      defaultValue: 'sales',
      group: groups.analysis,
    },
    {
      name: 'analysis.hasAlert',
      label: t('routing.fields.hasAlert', 'Has Alert'),
      inputType: 'select',
      values: [
        { name: 'true', label: t('values.yes', 'Yes') },
        { name: 'false', label: t('values.no', 'No') },
      ],
      defaultValue: 'false',
      group: groups.analysis,
    },

    // ============================================================
    // CRM Fields are loaded dynamically from API via useCrmFields hook
    // See: mergeCustomCrmFields() for dynamic field integration
    // ============================================================

    // ============================================================
    // Metadata
    // ============================================================
    {
      name: 'metadata.projectId',
      label: t('routing.fields.projectId', 'Project ID'),
      inputType: 'text',
      group: groups.metadata,
    },
    {
      name: 'metadata.dayOfWeek',
      label: t('routing.fields.dayOfWeek', 'Day of Week'),
      inputType: 'select',
      values: [
        { name: '0', label: t('routing.fields.sunday', 'Sunday') },
        { name: '1', label: t('routing.fields.monday', 'Monday') },
        { name: '2', label: t('routing.fields.tuesday', 'Tuesday') },
        { name: '3', label: t('routing.fields.wednesday', 'Wednesday') },
        { name: '4', label: t('routing.fields.thursday', 'Thursday') },
        { name: '5', label: t('routing.fields.friday', 'Friday') },
        { name: '6', label: t('routing.fields.saturday', 'Saturday') },
      ],
      defaultValue: '1',
      group: groups.metadata,
    },
    {
      name: 'metadata.hourOfDay',
      label: t('routing.fields.hourOfDay', 'Hour of Day (0-23)'),
      inputType: 'number',
      defaultValue: 12,
      group: groups.metadata,
    },
  ];
}

/**
 * Inbound routing fields — excludes "Analysis Results" group
 * because analysis hasn't been performed yet at inbound routing stage
 */
export function getInboundRoutingFields(t: TFunction<string>): Field[] {
  const groups = getRoutingFieldGroups(t);
  return getRoutingFields(t).filter((field) => field.group !== groups.analysis);
}

/**
 * Operators for routing conditions
 */
export function getRoutingOperators(t: TFunction<string>) {
  return [
    { name: '==', label: t('routing.operators.equals', 'equals') },
    { name: '!=', label: t('routing.operators.notEquals', 'not equals') },
    { name: '<', label: t('routing.operators.lessThan', 'less than') },
    { name: '<=', label: t('routing.operators.lessThanOrEqual', 'less than or equal') },
    { name: '>', label: t('routing.operators.greaterThan', 'greater than') },
    {
      name: '>=',
      label: t('routing.operators.greaterThanOrEqual', 'greater than or equal'),
    },
    {
      name: 'contains',
      label: t('routing.operators.contains', 'contains'),
    },
    {
      name: 'notContains',
      label: t('routing.operators.notContains', 'does not contain'),
    },
    {
      name: 'beginsWith',
      label: t('routing.operators.beginsWith', 'begins with'),
    },
    {
      name: 'endsWith',
      label: t('routing.operators.endsWith', 'ends with'),
    },
  ];
}

/**
 * Combinators (AND/OR)
 */
export function getRoutingCombinators(t: TFunction<string>) {
  return [
    { name: 'and', label: t('routing.operators.and', 'AND') },
    { name: 'or', label: t('routing.operators.or', 'OR') },
  ];
}

/**
 * Get fields grouped by category
 */
export function getGroupedRoutingFields(t: TFunction<string>): Record<string, Field[]> {
  const fields = getRoutingFields(t);
  const grouped: Record<string, Field[]> = {};

  for (const field of fields) {
    const group = (field.group as string) || 'Other';
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(field);
  }

  return grouped;
}

/**
 * Department info from CRM (subset of fields for UI)
 */
interface DepartmentOption {
  id: string;
  name: string;
  fullName: string;
}

/**
 * Enrich routing fields with department dropdown values
 * Finds record.departmentId field and populates its values from loaded departments.
 * Uses short name as label and stores full path in _departmentPaths for description display.
 */
export function enrichFieldsWithDepartments(
  fields: Field[],
  departments?: DepartmentOption[]
): Field[] {
  if (!departments || departments.length === 0) return fields;

  const pathMap: Record<string, string> = {};
  for (const d of departments) {
    pathMap[d.id] = d.fullName;
  }

  return fields.map((field) => {
    if (field.name === 'record.departmentId') {
      return {
        ...field,
        inputType: 'select' as const,
        values: departments.map((d) => ({
          name: d.id,
          label: d.name,
        })),
        _departmentPaths: pathMap,
      };
    }
    return field;
  });
}

/**
 * Map CRM field type to react-querybuilder inputType
 *
 * Priority:
 * 1. If hasValues (enumValues loaded) → select dropdown
 * 2. Text types (string, boolean) → text input
 * 3. Date types → date input
 * 4. Everything else (number, money, user, file, enum, unknown) → number input (ID)
 *
 * Fallback to 'number' for unknown types because most reference types
 * (tasks, users, leads, etc.) store entity IDs.
 */
function mapFieldTypeToInputType(
  fieldType: string,
  hasValues: boolean
): 'text' | 'number' | 'select' | 'date' {
  // Enum with loaded values → select dropdown
  if (hasValues) {
    return 'select';
  }

  // Map by field type
  switch (fieldType) {
    // Text types → text input
    case 'string':
    case 'boolean':
      return 'text';

    // Date types → date input
    case 'date':
    case 'datetime':
      return 'date';

    // Numeric and reference types → number input (ID)
    case 'number':
    case 'money':
    case 'user':
    case 'file':
    case 'enum': // Enum without loaded values → use number (ID input)
    default:
      // Fallback to number for unknown types (most reference types store IDs)
      return 'number';
  }
}

/**
 * Merge custom CRM fields into the base routing fields
 * Used when project has synced CRM field metadata
 *
 * Deduplication strategy:
 * - Fields from different entity types have unique names: crm.{entityType}.{fieldKey}
 * - User-facing labels include entity type for clarity when same fieldKey exists in multiple entities
 * - Fields are grouped by entity type in the UI
 */
export function mergeCustomCrmFields(
  customFields: Array<{
    fieldKey: string;
    fieldName: string;
    fieldType: string;
    entityType: string;
    possibleValues?: Array<{ value: string; label: string }>;
  }>,
  baseFields: Field[],
  t?: TFunction<string>
): Field[] {
  const merged = [...baseFields];
  const groups = t
    ? getRoutingFieldGroups(t)
    : {
        record: 'record',
        analysis: 'analysis',
        crm_deal: 'crm_deal',
        crm_lead: 'crm_lead',
        crm_contact: 'crm_contact',
        crm_company: 'crm_company',
        metadata: 'metadata',
      };

  // Count how many times each fieldKey appears across entity types
  const fieldKeyCount = new Map<string, number>();
  for (const field of customFields) {
    const count = fieldKeyCount.get(field.fieldKey) || 0;
    fieldKeyCount.set(field.fieldKey, count + 1);
  }

  for (const field of customFields) {
    const group =
      field.entityType === 'deal'
        ? groups.crm_deal
        : field.entityType === 'lead'
          ? groups.crm_lead
          : field.entityType === 'contact'
            ? groups.crm_contact
            : field.entityType === 'company'
              ? groups.crm_company
              : groups.metadata;

    const hasValues = Boolean(field.possibleValues?.length);
    const inputType = mapFieldTypeToInputType(field.fieldType, hasValues);

    // For enum fields without loaded values, add hint to label
    const isEnumWithoutValues = field.fieldType === 'enum' && !hasValues;

    // Add entity type suffix if same fieldKey exists in multiple entities (for search clarity)
    const isDuplicated = (fieldKeyCount.get(field.fieldKey) || 0) > 1;
    const entitySuffix = isDuplicated
      ? ` [${field.entityType.charAt(0).toUpperCase() + field.entityType.slice(1)}]`
      : '';
    const idSuffix = isEnumWithoutValues ? ' (ID)' : '';
    const label = `${field.fieldName}${idSuffix}${entitySuffix}`;

    const fieldDef: Field = {
      name: `crm.${field.entityType}.${field.fieldKey}`,
      label,
      inputType,
      group,
    };

    if (hasValues && field.possibleValues) {
      fieldDef.values = field.possibleValues.map((v) => ({
        name: v.value,
        label: v.label,
      }));
    }

    merged.push(fieldDef);
  }

  return merged;
}
